import { type Coordinate, CoordinatesUtils } from '../coordinates';
import { HttpService, ResponseType } from '../http';
import { SessionService } from '../session';
import { StringUtils } from '../string';
import { UriUtils } from '../uri';

export interface RawLocation {
  lat?: string;
  lon?: string;
  id: string;
  ref: string;
}

export interface LocationLight {
  id: string;
  ref: string;
}
export interface Location extends LocationLight {
  latitude?: number;
  longitude?: number;
}

export interface LocationWithCoordinate extends LocationLight, Coordinate {}

export interface LocationParams {
  latitude?: number;
  longitude?: number;
  storeId?: string;
  delivery?: string;
}

export interface LocatorServiceProps {
  getLocationsLight: () => Promise<LocationLight[]>;
  getLocations: () => Promise<Location[]>;
  getLocation: (params: LocationParams, fallbackLocationId?: string) => Promise<Location>;
  getLocationFromQueryParams: (fallbackLocationId?: string) => Promise<Location>;
}

const getLocationsLight = (url: string): Promise<LocationLight[]> =>
  HttpService.get<RawLocation[]>(url, { responseType: ResponseType.JSON });

const getLocations = async (url: string): Promise<Location[]> => {
  const rawData = await HttpService.get<RawLocation[]>(url, {
    responseType: ResponseType.JSON,
  });

  const parsedData = rawData.map(({ lat, lon, ref, id }) => ({
    latitude: StringUtils.toFloat(lat),
    longitude: StringUtils.toFloat(lon),
    ref,
    id,
  }));

  return parsedData;
};

const getLocationFromLocationsLight = async (
  storeId: string | undefined,
  delivery: string | undefined,
  url: string,
): Promise<LocationLight | undefined> => {
  if (!(storeId || delivery)) {
    return undefined;
  }

  const locationsLight: LocationLight[] = await getLocationsLight(url);
  let locationFromLight: LocationLight | undefined;

  if (storeId) {
    locationFromLight = locationsLight.find((location) => storeId === location.id);
  }

  if (!locationFromLight && delivery) {
    locationFromLight = locationsLight.find((location) => delivery === location.ref);
  }

  return locationFromLight;
};

const getLocationFromLocations = async (
  latitude: number | undefined,
  longitude: number | undefined,
  url: string,
  fallbackLocationId?: string,
): Promise<Location | undefined> => {
  const locations: Location[] = await getLocations(url);
  let locationFromLocations: Location | LocationWithCoordinate | undefined;

  if (latitude !== undefined && longitude !== undefined) {
    const locationsWithCoordinates = locations.filter(
      CoordinatesUtils.filterLocationsWithoutCoordinates,
    );
    const proximityCoordinate = CoordinatesUtils.findNearestCoordinate(locationsWithCoordinates, {
      latitude,
      longitude,
    });
    locationFromLocations = proximityCoordinate?.coordinate;
  }

  if (!locationFromLocations && fallbackLocationId) {
    locationFromLocations = locations.find((location) => fallbackLocationId === location.id);
  }

  if (!locationFromLocations) {
    locationFromLocations = locations?.[0];
  }

  return locationFromLocations;
};

const getLocation = async (
  { latitude, longitude, storeId: paramStoreId, delivery }: LocationParams,
  url: string,
  urlLight: string,
  fallbackLocationId?: string,
): Promise<Location> => {
  const sessionStoreId = SessionService.getSessionStoreId();
  const storeId = sessionStoreId ?? paramStoreId;

  try {
    const locationsLight = await getLocationFromLocationsLight(storeId, delivery, urlLight);
    const locations = await getLocationFromLocations(latitude, longitude, url, fallbackLocationId);
    const location = locationsLight ?? locations;

    if (!location) {
      throw new Error('No location found');
    }

    return location;
  } catch (e) {
    let errorMessage = "Couldn't determine location";

    if (typeof e === 'string') errorMessage = e;
    if (e instanceof Error) errorMessage = e.message;

    throw new Error(errorMessage);
  }
};

const getLocationFromQueryParams = (
  url: string,
  urlLight: string,
  fallbackStoreId?: string,
): Promise<Location> => {
  const queryParams = UriUtils.getQueryParams(window.location.href);
  const locationParams = {
    latitude: StringUtils.toFloat(queryParams?.lat),
    longitude: StringUtils.toFloat(queryParams?.lon),
    storeId: queryParams.storeId,
    delivery: queryParams.delivery,
  };

  return getLocation(locationParams, url, urlLight, fallbackStoreId);
};

export const LocatorService = (
  url = './data/locations.json',
  urlLight = './data/locations.light.json',
): LocatorServiceProps => ({
  getLocationsLight: () => getLocationsLight(urlLight),
  getLocations: () => getLocations(url),
  getLocation: (params: LocationParams, fallbackLocationId?: string): Promise<Location> =>
    getLocation(params, url, urlLight, fallbackLocationId),
  getLocationFromQueryParams: (fallbackStoreId?: string): Promise<Location> =>
    getLocationFromQueryParams(url, urlLight, fallbackStoreId),
});
